// Heuristic scoring used across the app. Pure, deterministic, no API calls.

// ----- LEAD HEAT (🔥 Hot / 🟡 Warm / ❄️ Cold) -----
export function leadHeat(lead) {
  if (!lead) return { tier: "cold", score: 0, label: "Cold", reason: "" };

  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  let score = 0;
  const reasons = [];

  const status = (lead.status || "").toLowerCase();
  if (status === "converted") {
    return { tier: "won", score: 100, label: "Won", reason: "Converted member" };
  }
  if (status === "trial_booked") { score += 30; reasons.push("trial booked"); }
  else if (status === "trial_done") { score += 25; reasons.push("trial done"); }
  else if (status === "contacted") { score += 12; reasons.push("contacted"); }
  else if (status === "new") { score += 5; reasons.push("new lead"); }
  else if (status === "lost") {
    return { tier: "cold", score: 0, label: "Lost", reason: "Lost lead" };
  }

  // Trial activity
  const trialDate = lead.trial_date ? new Date(lead.trial_date).getTime() : null;
  if (trialDate) {
    const daysToTrial = Math.round((trialDate - now) / oneDay);
    if (daysToTrial >= 0 && daysToTrial <= 3) { score += 25; reasons.push("trial in next 3d"); }
    else if (daysToTrial > 3) { score += 12; reasons.push("trial scheduled"); }
  }
  if (lead.trial_status === "attended") { score += 25; reasons.push("attended trial"); }
  if (lead.trial_status === "no_show") { score -= 15; reasons.push("trial no-show"); }

  // Source weight
  const source = (lead.source || "").toLowerCase();
  if (source.includes("referral")) { score += 18; reasons.push("referral"); }
  else if (source.includes("ad") || source.includes("meta")) { score += 12; reasons.push("ad source"); }

  // Recency - last message timestamp
  const msgs = lead.messages || [];
  const lastMsg = msgs.length > 0 ? new Date(msgs[msgs.length - 1].created_at).getTime() : null;
  if (lastMsg) {
    const daysAgo = Math.floor((now - lastMsg) / oneDay);
    if (daysAgo <= 1) { score += 15; reasons.push("messaged today"); }
    else if (daysAgo <= 3) { score += 8; reasons.push("messaged this week"); }
    else if (daysAgo > 7) { score -= 5; reasons.push("stale conversation"); }
  } else {
    score -= 5;
    reasons.push("no contact yet");
  }

  // Recency - created_at (cold leads get colder over time)
  if (lead.created_at) {
    const ageDays = Math.floor((now - new Date(lead.created_at).getTime()) / oneDay);
    if (ageDays > 14 && status === "new") { score -= 10; reasons.push(`${ageDays}d old, no action`); }
  }

  score = Math.max(0, Math.min(100, score));

  let tier = "cold", label = "Cold";
  if (score >= 55) { tier = "hot"; label = "Hot"; }
  else if (score >= 25) { tier = "warm"; label = "Warm"; }

  return { tier, score, label, reason: reasons.slice(0, 2).join(" · ") };
}

// ----- MEMBER RISK (low / medium / high) -----
// PHASE A NOTE: Backend now sends `risk_level` and `risk_factors` directly.
// Prefer those when present; fall back to the local heuristic for older
// data shapes (e.g. portal endpoint that doesn't run normalize_member yet).
export function memberRisk(member) {
  if (!member) return { tier: "low", label: "Active", reason: "" };

  // Backend-computed signal - authoritative.
  if (member.risk_level) {
    const tierMap = { healthy: "low", at_risk: "medium", high_risk: "high" };
    const labelMap = { healthy: "Active", at_risk: "At risk", high_risk: "High risk" };
    const tier = tierMap[member.risk_level] || "low";
    return {
      tier,
      label: labelMap[member.risk_level] || "Active",
      reason: (member.risk_factors || []).slice(0, 2).join(" · "),
    };
  }

  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  const expiry = member.expiry_date ? new Date(member.expiry_date).getTime() : null;
  const lastVisit = member.last_visit ? new Date(member.last_visit).getTime() : null;
  const daysSinceVisit = lastVisit ? Math.floor((now - lastVisit) / oneDay) : null;
  const daysToExpiry = expiry ? Math.floor((expiry - now) / oneDay) : null;

  if (daysToExpiry !== null && daysToExpiry < 0) {
    return { tier: "high", label: "Expired", reason: `expired ${-daysToExpiry}d ago` };
  }
  if (daysSinceVisit === null) {
    return { tier: "high", label: "No visits", reason: "never checked in" };
  }
  if (daysSinceVisit > 21) {
    return { tier: "high", label: "High risk", reason: `${daysSinceVisit}d inactive` };
  }
  if (daysSinceVisit > 10) {
    return { tier: "medium", label: "At risk", reason: `${daysSinceVisit}d inactive` };
  }
  if (daysToExpiry !== null && daysToExpiry <= 7) {
    return { tier: "medium", label: "Expiring", reason: `${daysToExpiry}d left` };
  }
  return { tier: "low", label: "Active", reason: "" };
}

// ----- TRIAL CONVERSION LIKELIHOOD -----
export function trialConversionLikelihood(lead) {
  if (!lead) return { tier: "low", label: "Low", reason: "" };

  if (lead.status === "converted") return { tier: "won", label: "Converted", reason: "" };
  if (lead.trial_status === "no_show") return { tier: "low", label: "Low", reason: "no-show" };
  if (lead.trial_status === "attended") {
    const heat = leadHeat(lead);
    if (heat.score >= 70) return { tier: "high", label: "High", reason: "attended + engaged" };
    return { tier: "medium", label: "Medium", reason: "attended, needs nudge" };
  }
  if (lead.trial_status === "pending" && lead.trial_date) {
    const daysToTrial = Math.round((new Date(lead.trial_date).getTime() - Date.now()) / 86400000);
    if (daysToTrial >= 0 && daysToTrial <= 2) return { tier: "medium", label: "Medium", reason: "trial soon - confirm" };
    return { tier: "medium", label: "Medium", reason: "trial scheduled" };
  }
  return { tier: "low", label: "Low", reason: "no trial yet" };
}
